// src/api/volumeDiscountTemplates.ts
const BASE_URL =
  (process.env.REACT_APP_API_URL?.replace(/\/+$/, "") ||
    "http://localhost:5002");

// Join segments and collapse duplicate slashes (keeps http://)
function joinUrl(...parts: Array<string | number>): string {
  return parts
    .map(String)
    .join("/")
    .replace(/(?<!:)\/\/+/g, "/");
}
// ^ make sure these imports match your project

async function apiRequest<T>(
  method: "GET" | "POST" | "PATCH" | "DELETE",
  url: string,
  token?: string | null,
  body?: any,
  signal?: AbortSignal
): Promise<T> {
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const init: RequestInit = { method, headers, signal };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }

  const res = await fetch(url, init);

  let json: any = null;
  try {
    json = await res.json();
  } catch {
    // non-JSON response; leave json as null
  }

  if (!res.ok) {
    const msg =
      json?.message ||
      json?.error ||
      `Request failed: ${res.status} ${res.statusText}`;
    const err = new Error(msg) as Error & { status?: number; payload?: any };
    err.status = res.status;
    err.payload = json;
    throw err;
  }

  return (json?.data ?? json) as T;
}

export type VolumeTemplateCreatePayload = {
  name: string;
  starting_at: number;
  discount: number;
  discount_in: "%" | "$";
  status: "active" | "inactive";
  priority?: number;
};

export type VolumeTemplate = {
  _id: string;
  name: string;
  starting_at: number;
  discount: number;
  discount_in: "%" | "$";
  status: "active" | "inactive";
  priority?: number;
  created_at?: string;
  updated_at?: string;
};

export type VolumeAssignCreatePayload = {
  product_id: string;
  variant_id?: string | null;
  apply_to_all_variants: boolean;
  status: "active" | "inactive";
  // priority belongs on template; some backends allow per-assignment priority too — omit unless supported
};

export type VolumeAssignment = {
  id: string;
  product_id: string;
  variant_id?: string | null;
  volume_discount_template_id: string;
  apply_to_all_variants: boolean;
  status: "active" | "inactive";
  created_at?: string;
  updated_at?: string;
};

export type AssignmentsDatatableQuery = {
  page?: number;
  page_size?:number;
  limit?: number;
  include_templates?:boolean,
  search?: string;
  status?: "active" | "inactive";
  sort?: { field: string; order: "asc" | "desc" };
};

/* ================================
   Templates
================================== */

/** 1) Create Volume Discount Template */
export async function createVolumeDiscountTemplate(
  payload: VolumeTemplateCreatePayload,
  token?: string | null,
  signal?: AbortSignal
): Promise<VolumeTemplate> {
  const url = joinUrl(BASE_URL, "volume-discounts", "templates");
  // normalize discount_in if "$" provided
  if ((payload as any)?.discount_in === "$") {
    (payload as any).discount_in = "$";
  }
  return apiRequest<VolumeTemplate>("POST", url, token, payload, signal);
}

/* ================================
   Assignments
================================== */

/** 2) Assign Template → Product/Variant */
export async function createVolumeDiscountAssignment(
  templateId: string,
  payload: VolumeAssignCreatePayload,
  token?: string | null,
  signal?: AbortSignal
): Promise<VolumeAssignment> {
  const url = joinUrl(BASE_URL, "volume-discounts", templateId, "assignments");
  return apiRequest<VolumeAssignment>("POST", url, token, payload, signal);
}

/** 4) Get Assignments for a Variant */
export async function getAssignmentsForVariant(
  productId: string,
  variantId: string,
  token?: string | null,
  signal?: AbortSignal
): Promise<VolumeAssignment[]> {
  const url = joinUrl(
    BASE_URL,
    "products",
    productId,
    "variants",
    variantId,
    "volume-discount-assignments"
  );
  const res = await apiRequest<any>("GET", url, token, undefined, signal);
  // API may return {data: []} or []
  return Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
}

/** 5) Update Assignment */
export async function updateVolumeDiscountTemplates(
  discountId: string,
  patch: Partial<Pick<VolumeAssignment, "status"> & { priority?: number }>,
  token?: string | null,
  signal?: AbortSignal
): Promise<VolumeAssignment> {
  const url = joinUrl(BASE_URL, "volume-discounts/templates", discountId);
  return apiRequest<VolumeAssignment>("PATCH", url, token, patch, signal);
}

/** 5) Update Assignment (e.g. change variant/combination or status) */
export async function updateVolumeDiscountAssignment(
  id: string,
  Assignmentpatch: Partial<Pick<VolumeAssignment, "variant_id" | "apply_to_all_variants" | "status"> & { priority?: number }>,
  token?: string | null,
  signal?: AbortSignal
): Promise<VolumeAssignment> {
  const url = joinUrl(BASE_URL, "volume-discount-assignments", id);
  return apiRequest<VolumeAssignment>("PATCH", url, token, Assignmentpatch, signal);
}


/** 6) Delete Assignment */
export async function deleteVolumeDiscountAssignment(
  discountId: string,
  token?: string | null,
  signal?: AbortSignal
): Promise<void> {
  const url = joinUrl(BASE_URL, "volume-discount-assignments", discountId);
  await apiRequest<void>("DELETE", url, token, undefined, signal);
}

export async function getAssignmentsDatatable(
  productId: string,
  body: AssignmentsDatatableQuery,
  token?: string | null,
  signal?: AbortSignal
): Promise<{ rows: VolumeAssignment[]; total?: number }> {
  const url = joinUrl(BASE_URL, "products", productId, "volume-discount-assignments", "datatable");
  const res = await apiRequest<any>("POST", url, token, body, signal);
  // normalize; your API may return { data, total } or { rows, total } etc.
  if (Array.isArray(res?.data)) return { rows: res.data, total: res.total };
  if (Array.isArray(res?.rows)) return { rows: res.rows, total: res.total };
  if (Array.isArray(res)) return { rows: res, total: undefined };
  return { rows: [], total: 0 };
}
